<template>
  <v-expansion-panels v-if="teamId != ''" v-model="openedPanels">
    <v-expansion-panel v-for="(group, index) in criteriaData" :key="index">
      <v-expansion-panel-title :color="group.color">
        {{ group.group_name }}
        <v-badge
          v-if="!addingOptional && getSelectedCriteriaCount(group.group_name) > 0"
          color="primary"
          :content="getSelectedCriteriaCount(group.group_name)"
          class="ml-3"></v-badge>
      </v-expansion-panel-title>

      <v-expansion-panel-text>
        <v-row v-for="(criteria, idx) in group.criterias" :key="idx">
          <v-col>
            <v-card>
              <v-card-title class="d-flex justify-space-between align-center">
                <span>#{{ criteria.number }}: {{ criteria.name }}</span>
                <v-btn
                  color="primary"
                  variant="flat"
                  elevation="2"
                  v-if="showSave && isCriteriaSelected(criteria.id)"
                  @click="emit('save')"
                  >Save</v-btn
                >
              </v-card-title>
              <v-card-subtitle>
                <div
                  v-if="hideOpt ? !getIsMandatory(criteria.id) : false"
                  class="w-100 text-wrap font-weight-bold text-red">
                  This can no longer be picked.
                </div>
                <div v-else>({{ !addingOptional ? criteria.left() : '' }} left)</div>
              </v-card-subtitle>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="1">
                    <v-checkbox-btn
                      v-if="hideOpt ? getIsMandatory(criteria.id) : true"
                      :model-value="isCriteriaSelected(criteria.id)"
                      @click="toggleSelection(criteria, getIsMandatory(criteria.id))" />
                    <div v-else>
                      <v-icon icon="mdi-alpha-x-circle" color="danger"></v-icon>
                    </div>
                  </v-col>
                  <v-col
                    cols="12"
                    :md="!addingOptional && (!teamInitSelect || authStore.isAdmin) ? 6 : 8">
                    <div
                      class="w-100 text-wrap font-weight-light"
                      v-html="criteria.description"></div>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-img :src="criteria.thubmnail?.url">
                      <template #sources>
                        <source :srcset="criteria.thubmnail?.webp_url" />
                      </template>
                    </v-img>
                  </v-col>
                  <v-col cols="12" md="1">
                    <v-btn
                      v-if="criteria.video?.url"
                      icon
                      variant="text"
                      :color="criteria.video ? 'primary' : 'secondary'"
                      @click="openModal(criteria)">
                      <v-icon icon="mdi-video"></v-icon>
                    </v-btn>
                  </v-col>

                  <v-col
                    cols="12"
                    md="2"
                    v-if="!addingOptional && (!teamInitSelect || authStore.isAdmin)">
                    <v-list-item-action end>
                      <v-switch
                        :label="getIsMandatory(criteria.id) ? 'Mandatory' : 'Optional'"
                        :model-value="getIsMandatory(criteria.id)"
                        :disabled="
                          !isCriteriaSelected(criteria.id) || isCriteriaLocked(criteria.id)
                        "
                        @click="toggleMandatory(criteria.id)" />
                    </v-list-item-action>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
  <v-dialog v-model="videoOpen">
    <AssetPreviewComponent
      :asset="selectedAsset"
      type="video"
      :isModal="true"
      :disableUpload="true"
      :allowDownload="false"
      @assetCanceled="videoOpen = false"></AssetPreviewComponent>
  </v-dialog>
</template>
<script setup lang="ts">
import {
  ITeamCriteriaSelectionDTO,
  TeamCriteriaSelectionDTO,
  ICriteriaDTO,
  ICriteriaGroupDTO,
  IAssetDTO,
  AssetDTO,
} from '@/dtos';
import { useAuthStore } from '@/stores';
import { computed, ref, watch } from 'vue';
import AssetPreviewComponent from '../ui/AssetPreviewComponent.vue';

interface Props {
  teamId: string;
  teamInitSelect?: boolean;
  teamCriteriaData: ITeamCriteriaSelectionDTO[];
  criteriaData: ICriteriaGroupDTO[];
  addingOptional?: boolean;
  showSave?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits(['toggleSelection', 'save']);

const teamId = computed(() => props.teamId);
const teamInitSelect = computed(() => props.teamInitSelect ?? false);
const addingOptional = computed(() => props.addingOptional ?? false);
const showSave = computed(() => props.showSave ?? false);
const authStore = useAuthStore();

const videoOpen = ref(false);
const selectedAsset = ref<IAssetDTO>(new AssetDTO());
const openedPanels = ref<number[]>([]);
const hideOpt = ref(true);

// --- normalize props into DTOs ---
const teamCriteriaData = computed(() =>
  props.teamCriteriaData.map((c) => new TeamCriteriaSelectionDTO(c)),
);

const selectedCriteria = ref<ITeamCriteriaSelectionDTO[]>([]);
watch(
  teamCriteriaData,
  (newVal) => {
    selectedCriteria.value = [...newVal];
    openedPanels.value.push(0);
  },
  { immediate: true },
);

const criteriaData = computed(() => props.criteriaData ?? []);

// --- helper sets/maps for fast lookups ---
const selectedCriteriaIds = computed(
  () => new Set(selectedCriteria.value.map((c) => c.criteria?.id)),
);

const criteriaMap = computed(() => {
  const map = new Map<string, ICriteriaDTO>();
  criteriaData.value.forEach((g) => g.criterias.forEach((c) => map.set(c.id, c)));
  return map;
});

// --- methods ---
function getSelectedCriteriaCount(groupName: string) {
  return selectedCriteria.value.filter((sel) => sel.criteria?.group?.group_name === groupName)
    .length;
}

function isCriteriaSelected(criteriaId: string) {
  return selectedCriteriaIds.value.has(criteriaId);
}

function isCriteriaLocked(criteriaId: string) {
  const prev = teamCriteriaData.value.find((c) => c.criteria?.id === criteriaId);
  const match = criteriaMap.value.get(criteriaId);
  return (!prev || prev.mandatory !== true) && match && match.left() <= 0;
}

function getIsMandatory(criteriaId: string) {
  if (isCriteriaLocked(criteriaId)) return false;
  const found = selectedCriteria.value.find((c) => c.criteria?.id === criteriaId);
  return found?.mandatory ?? true; // default true
}

function toggleMandatory(criteriaId: string) {
  const idx = selectedCriteria.value.findIndex((c) => c.criteria?.id === criteriaId);
  if (idx > -1) selectedCriteria.value[idx].mandatory = !selectedCriteria.value[idx].mandatory;
}

function toggleSelection(criteria: ICriteriaDTO, isMandatory: boolean) {
  const alreadySelected = isCriteriaSelected(criteria.id);
  emit('toggleSelection', criteria, alreadySelected ? undefined : isMandatory);
}

function openModal(criteria: ICriteriaDTO) {
  if (criteria.video) {
    selectedAsset.value = criteria.video;
    videoOpen.value = true;
  }
}
</script>
